import { Directive, ElementRef, HostListener, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** `2026-10-01T14:30` in the browser's zone, the only shape `datetime-local` accepts. */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * A `datetime-local` input whose model value is an ISO instant.
 *
 * The native input speaks local wall-clock time with no zone; the backend validates an instant with an
 * offset. Converting here, once, keeps every form that uses the control from doing it differently.
 */
@Directive({
  selector: 'input[eeDatetimeInput]',
  standalone: true,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => DatetimeInputDirective), multi: true }]
})
export class DatetimeInputDirective implements ControlValueAccessor {
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private readonly el: ElementRef<HTMLInputElement>) {
    el.nativeElement.type = 'datetime-local';
  }

  writeValue(value: string | null): void {
    this.el.nativeElement.value = toLocalInput(value);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.el.nativeElement.disabled = disabled;
  }

  @HostListener('input')
  protected onInput(): void {
    const raw = this.el.nativeElement.value;
    const d = raw ? new Date(raw) : null;
    this.onChange(d && !isNaN(d.getTime()) ? d.toISOString() : null);
  }

  @HostListener('blur')
  protected onBlur(): void {
    this.onTouched();
  }
}
